import SearchBox from "@/common/SearchBox";
import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState, useEffect } from "react";

import { useBusinessMypageCustomerList } from "~/services/business/myPage";

export const Route = createFileRoute(
  "/business/layout/user-customer-management",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { history } = useRouter();
  const [selectedSearchTypeFilter, setSelectedSearchTypeFilter] = useState<{
    label: string;
    value: string | null;
  }>({ label: "전체", value: null });

  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [currentSearchKeyword, setCurrentSearchKeyword] = useState<string>("");
  const [isSearchTriggered, setIsSearchTriggered] = useState<boolean>(false);

  const [page, setPage] = useState<number>(1);
  const [accumulatedCustomers, setAccumulatedCustomers] = useState<
    NonNullable<
      ReturnType<typeof useBusinessMypageCustomerList>["data"]
    >["data"]["customerPage"]["content"]
  >([]);

  // 필터 옵션
  const categorys = [
    { label: "전체", value: "RESERV_TYPE_ALL" },
    { label: "유치원", value: "RESERV_TYPE_CLASS" },
    { label: "호텔", value: "RESERV_TYPE_HOTEL" },
  ];

  console.log(isSearchTriggered);

  const handleSearchTypeClick = (type: {
    label: string;
    value: string | null;
  }) => {
    setSelectedSearchTypeFilter(type);
  };

  const handleSearch = (keyword: string) => {
    setCurrentSearchKeyword(keyword);
    setIsSearchTriggered(true);
  };

  // 고객 정보 상세 페이지로 이동
  const goToCustomerDetail = (userSeq: number, petSeq: string) => {
    history.push(
      `/business/layout/user-customer-management-detail?userSeq=${userSeq}&petSeq=${petSeq}`,
    );
  };

  // selectedSearchTypeFilter가 변경될 때마다 API 호출
  useEffect(() => {
    setIsSearchTriggered(true);
  }, [selectedSearchTypeFilter]);

  // 필터/검색어 변경 시 페이지 및 누적 목록 초기화
  useEffect(() => {
    setPage(1);
    setAccumulatedCustomers([]);
  }, [selectedSearchTypeFilter, currentSearchKeyword]);

  // API 호출을 위한 파라미터
  const apiParams = {
    searchKeyword: currentSearchKeyword || undefined,
    reservType: selectedSearchTypeFilter.value || "RESERV_TYPE_ALL",
    page,
    size: 100,
  };

  // 고객 목록 API 호출
  const { data: customerListData } = useBusinessMypageCustomerList(apiParams);

  // API 응답을 누적 목록에 반영 (첫 페이지는 교체, 이후 페이지는 추가)
  useEffect(() => {
    const pageData = customerListData?.data.customerPage;
    if (!pageData) return;
    setAccumulatedCustomers((prev) =>
      pageData.first ? pageData.content : [...prev, ...pageData.content],
    );
  }, [customerListData]);

  return (
    <>
      <LayoutHeader title={"고객정보 목록"} hasBackBtn hasFootPrint={false} />
      <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5]">
        <div className="grid grid-cols-1 ml-4">
          <div className="flex overflow-x-auto gap-2 mt-3 w-full whitespace-nowrap">
            {categorys.map((category) => (
              <button
                key={category.label}
                onClick={() => handleSearchTypeClick(category)}
                className={`
                    px-4 py-1 rounded-full border text-sm font-semibold h-10 min-w-[100px]
                    bg-white
                    ${
                      selectedSearchTypeFilter.label === category.label
                        ? "border-orange-400 text-orange-400"
                        : "border-transparent text-gray-700 border-[#CCC]"
                    }
                      `}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
        {/* 서치박스 */}
        <div className="mx-2 mt-2">
          <div className="flex justify-end items-center px-2 py-3">
            <SearchBox
              searchText={searchKeyword}
              setSearchText={setSearchKeyword}
              onSearch={handleSearch}
              placeholder="반려동물 이름 또는 보호자 닉네임으로 검색"
            />
          </div>
        </div>
        {/* 총 마리수 */}
        <div className="flex gap-2 items-center pl-1 mx-4 mb-2">
          <div className="text-[14px] font-semibold">
            총 {customerListData?.data.totalPetCount || 0}마리
          </div>
          <div className="text-[13px] text-[#999999]">|</div>
          <div className="text-[13px] text-[#999999]">
            반려동물의 수로 집계됩니다.
          </div>
        </div>
        <div className="grid grid-cols-8 gap-2 items-center mx-4 mb-2">
          <div className="col-span-2 text-[14px] text-[#666666] text-center">
            고객명
          </div>
          <div className="col-span-3 text-[13px] text-[#666666] text-center">
            반려동물
          </div>
          <div className="col-span-3 text-[14px] text-[#666666] text-center">
            마지막 이용일
          </div>
        </div>
        {/* 고객 목록 */}
        {accumulatedCustomers.map((customer) => (
          <div className="mx-4 mb-2" key={customer.userSeq}>
            {customer.pets.map((pet, index) => (
              <div key={pet.petSeq}>
                <div
                  className={`grid grid-cols-8 gap-2 items-center bg-white h-[50px] ${
                    customer.pets.length === 1
                      ? "rounded-lg border border-[#e9e9e9]"
                      : index === 0
                        ? "rounded-t-lg border-t border-l border-r border-[#e9e9e9]"
                        : index === customer.pets.length - 1
                          ? "rounded-b-lg border-b border-l border-r border-[#e9e9e9]"
                          : "border-l border-r border-[#e9e9e9]"
                  }`}
                >
                  <button
                    className="col-span-2 text-[15px] text-[#111111] text-center"
                    onClick={() =>
                      goToCustomerDetail(customer.userSeq, pet.petSeq.toString())
                    }
                  >
                    {index === 0 ? customer.userName : ""}
                  </button>
                  <button
                    className="col-span-3 text-[14px] text-[#111111] text-left overflow-hidden text-ellipsis whitespace-nowrap"
                    onClick={() =>
                      goToCustomerDetail(customer.userSeq, pet.petSeq.toString())
                    }
                  >
                    {pet.petName}
                  </button>
                  <div className="col-span-3 text-[14px] text-[#111111] text-center">
                    {pet.registDate}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
        {!customerListData?.data.customerPage.last && (
          <div className="flex justify-center">
            <button
              className="py-2 mr-2 ml-2 w-full bg-white rounded border border-gray-400"
              onClick={() => setPage((prev) => prev + 1)}
            >
              <p>더보기</p>
            </button>
          </div>
        )}
      </div>
    </>
  );
}
